import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import { timesheetsAPI as tsAPI } from '../../services/api';
import { getTodayYMDInTimezone } from '../../utils/dateTimeUtils';
import { useAuth } from '../../contexts/AuthContext';
import { weekStatusLabel } from '../Payroll/plainLabels';
import TimesheetWeekGrid from './TimesheetWeekGrid';

function addDaysYmd(ymd, days) {
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + days));
  return dt.toISOString().slice(0, 10);
}

function mondayOf(ymd) {
  const [y, m, d] = ymd.split('-').map(Number);
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  const fromMon = dow === 0 ? 6 : dow - 1;
  return addDaysYmd(ymd, -fromMon);
}

export default function TimesheetPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const today = getTodayYMDInTimezone(user?.timezone);
  const [weekStart, setWeekStart] = useState(mondayOf(today));
  const [addOpen, setAddOpen] = useState(false);
  const [draft, setDraft] = useState({
    workDate: today,
    hours: 1,
    projectId: '',
    activityTypeId: '',
    title: '',
    remarks: '',
    leaveTemplateLineId: '',
    daySpan: 'full',
  });

  const weekQuery = useQuery(
    ['timesheet-week', weekStart],
    async () => {
      const res = await tsAPI.getMyWeek({ weekStart });
      return res.data.data;
    },
    { keepPreviousData: true }
  );

  const catalogs = useQuery(['timesheet-catalogs'], async () => {
    const [p, a] = await Promise.all([tsAPI.getProjects(), tsAPI.getActivityTypes()]);
    return { projects: p.data.data || [], activities: a.data.data || [] };
  });

  const refreshMut = useMutation(() => tsAPI.refreshWeek(weekQuery.data.id), {
    onSuccess: () => {
      qc.invalidateQueries(['timesheet-week']);
      toast.success('Refreshed from system');
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Refresh failed'),
  });

  const submitMut = useMutation(() => tsAPI.submitWeek(weekQuery.data.id), {
    onSuccess: () => {
      qc.invalidateQueries(['timesheet-week']);
      toast.success('Submitted for approval');
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Submit failed'),
  });

  const patchMut = useMutation(
    ({ lineId, body }) => tsAPI.patchLine(weekQuery.data.id, lineId, body),
    {
      onSuccess: () => qc.invalidateQueries(['timesheet-week']),
      onError: (e) => toast.error(e.response?.data?.message || 'Update failed'),
    }
  );

  const addMut = useMutation(
    async () => {
      const res = await tsAPI.addLine(weekQuery.data.id, draft.leaveTemplateLineId
        ? {
            workDate: draft.workDate,
            leaveTemplateLineId: draft.leaveTemplateLineId,
            daySpan: draft.daySpan,
            remarks: draft.remarks || undefined,
          }
        : {
            workDate: draft.workDate,
            hours: Number(draft.hours),
            projectId: draft.projectId || null,
            activityTypeId: draft.activityTypeId || null,
            title: draft.title || undefined,
            remarks: draft.remarks || undefined,
          });
      return res.data?.data;
    },
    {
      onSuccess: (data) => {
        setAddOpen(false);
        qc.invalidateQueries(['timesheet-week']);
        toast.success(data?.leavePendingMessage || 'Line added');
      },
      onError: (e) => toast.error(e.response?.data?.message || 'Add failed'),
    }
  );

  const deleteMut = useMutation((lineId) => tsAPI.deleteLine(weekQuery.data.id, lineId), {
    onSuccess: () => {
      qc.invalidateQueries(['timesheet-week']);
      toast.success('Line removed');
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Delete failed'),
  });

  const week = weekQuery.data;
  const editable = week?.editable;

  return (
    <Box sx={{ p: 2, maxWidth: 1200, mx: 'auto' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2} mb={2}>
        <Box>
          <Typography variant="h5">Timesheet</Typography>
          <Typography variant="body2" color="text.secondary">
            Fill this week, then send it for approval. An earlier day in a month whose salary is still open can be requested from My leave. A processed month cannot be changed.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            size="small"
            disabled={Boolean(week?.earliestViewStart && weekStart <= week.earliestViewStart)}
            onClick={() => setWeekStart(addDaysYmd(weekStart, -7))}
          >
            Previous week
          </Button>
          <TextField
            size="small"
            type="date"
            label="Week of"
            InputLabelProps={{ shrink: true }}
            value={weekStart}
            onChange={(e) => {
              const next = mondayOf(e.target.value);
              const earliest = week?.earliestViewStart;
              const latest = week?.currentWeekStart;
              if (earliest && next < earliest) {
                setWeekStart(earliest);
                return;
              }
              if (latest && next > latest) {
                setWeekStart(latest);
                return;
              }
              setWeekStart(next);
            }}
          />
          <Button
            size="small"
            disabled={Boolean(week?.currentWeekStart && weekStart >= week.currentWeekStart)}
            onClick={() => setWeekStart(addDaysYmd(weekStart, 7))}
          >
            Next week
          </Button>
        </Stack>
      </Stack>

      {weekQuery.isLoading && (
        <Box textAlign="center" py={6}>
          <CircularProgress />
        </Box>
      )}
      {weekQuery.isError && (
        <Alert severity="error">{weekQuery.error?.response?.data?.message || 'Failed to load'}</Alert>
      )}

      {week && (
        <>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between">
              <Box>
                <Typography variant="subtitle1">
                  {week.weekStart} → {week.weekEnd} ({week.timezone})
                </Typography>
                <Chip size="small" label={weekStatusLabel(week.status)} sx={{ mt: 1 }} />
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Expected {week.expectedHours} hours · Worked {week.workedHours} · Leave {week.leaveHours} ·
                  Difference {week.varianceHours}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {week.mode === 'teacher' && editable && (
                  <Button
                    variant="outlined"
                    onClick={() => refreshMut.mutate()}
                    disabled={refreshMut.isLoading}
                  >
                    Load classes
                  </Button>
                )}
                {editable && (
                  <Button variant="outlined" onClick={() => setAddOpen(true)}>
                    Add a day
                  </Button>
                )}
                {editable && (
                  <Button
                    variant="contained"
                    onClick={() => submitMut.mutate()}
                    disabled={submitMut.isLoading}
                  >
                    Send for approval
                  </Button>
                )}
              </Stack>
            </Stack>
            {(week.warnings || []).map((w) => (
              <Alert key={w.code + w.message} severity="info" sx={{ mt: 1 }}>
                {w.message}
              </Alert>
            ))}
            {week.rejectionReason && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                Rejection: {week.rejectionReason}
              </Alert>
            )}
            {!editable && ['draft', 'rejected'].includes(week.status) ? (
              <Alert severity="info" sx={{ mt: 1 }}>
                You can view weeks from the past year. Edit and submit are only allowed for the current
                week and the previous week.
              </Alert>
            ) : null}
          </Paper>

          <TimesheetWeekGrid
            week={week}
            editable={editable}
            onPatch={(lineId, body) => patchMut.mutate({ lineId, body })}
            onDelete={(lineId) => deleteMut.mutate(lineId)}
            onConfirm={(lineId, confirmationStatus) =>
              patchMut.mutate({ lineId, body: { confirmationStatus } })
            }
            emptyMessage="Nothing is filled yet. Add a day, or teachers can press Load classes."
            hint="Each box is the total for that day. Click the hours to see the detail."
          />
        </>
      )}

      <Dialog open={addOpen} onClose={() => setAddOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add a day</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              type="date"
              label="Date"
              InputLabelProps={{ shrink: true }}
              value={draft.workDate}
              onChange={(e) => setDraft({ ...draft, workDate: e.target.value })}
            />
            <TextField
              select
              label="Leave type"
              helperText={
                week?.mode === 'teacher'
                  ? 'Sends pay leave for approval and adds it to the timesheet when approved. Class cover stays on Class leave.'
                  : 'Choose this only if you did not already apply for leave. Days off and holidays are not leave. A processed salary month cannot be changed.'
              }
              value={draft.leaveTemplateLineId}
              onChange={(e) => setDraft({ ...draft, leaveTemplateLineId: e.target.value })}
            >
              <MenuItem value="">Not leave</MenuItem>
              {(week?.leaveTypes || []).map((t) => (
                <MenuItem key={t.id} value={t.id}>{t.leaveTypeName}</MenuItem>
              ))}
            </TextField>
            {draft.leaveTemplateLineId ? (
              <TextField
                select
                label="Day"
                value={draft.daySpan}
                onChange={(e) => setDraft({ ...draft, daySpan: e.target.value })}
              >
                <MenuItem value="full">Full day</MenuItem>
                <MenuItem value="half">Half day</MenuItem>
              </TextField>
            ) : (
            <TextField
              type="number"
              label="Hours"
              value={draft.hours}
              onChange={(e) => setDraft({ ...draft, hours: e.target.value })}
            />
            )}
            {!draft.leaveTemplateLineId && (
              <>
                <TextField
                  select
                  label="Project"
                  value={draft.projectId}
                  onChange={(e) => setDraft({ ...draft, projectId: e.target.value })}
                >
                  <MenuItem value="">None</MenuItem>
                  {(catalogs.data?.projects || []).map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  label="Activity"
                  value={draft.activityTypeId}
                  onChange={(e) => setDraft({ ...draft, activityTypeId: e.target.value })}
                >
                  <MenuItem value="">None</MenuItem>
                  {(catalogs.data?.activities || []).map((a) => (
                    <MenuItem key={a.id} value={a.id}>
                      {a.name}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="Title"
                  value={draft.title}
                  onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                />
              </>
            )}
            <TextField
              label="Note"
              multiline
              minRows={2}
              value={draft.remarks}
              onChange={(e) => setDraft({ ...draft, remarks: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => addMut.mutate()} disabled={addMut.isLoading}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
